import { LoggerService } from '../logger/logger.service';
import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
	EventMessage,
	EventType,
	InteractionStatus,
	AuthenticationResult
} from '@azure/msal-browser';
import { TokenClaims } from '@azure/msal-common';
import {
	BehaviorSubject,
	filter,
	lastValueFrom,
	Observable,
	Subject,
	Subscriber,
	takeUntil
} from 'rxjs';
import { b2cPolicies } from '../../injection-tokens/auth-config.injection-token';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RoutePermissionAction, RoutePermissionCategory, RoutePermissionGroup, RoutePermissions } from '../../guards/permission/definitions';
import { ApiResponse } from '../../definitions/api-response.interface';
import { ApiService } from '../api/api.service';
import { hasIn as lodashHasIn, cloneDeep as lodashCloneDep, get as lodashGet } from 'lodash';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { ShellService } from '../../components/shell/shell.service';

type PermissionsMap = Map<string, RoutePermissions>;
@Injectable({
	providedIn: 'root'
})
export class AuthenticationService
{
	dataSource!: { id: number; claim: string; value: unknown }[];
	private readonly pDestroying = new Subject<void>();
	isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public fetchedPermissions$: BehaviorSubject<PermissionsMap> = new BehaviorSubject<PermissionsMap>(new Map<string, RoutePermissions>());

	constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly loggerService: LoggerService,
    private readonly localStorageService: LocalStorageService,
    private readonly apiService: ApiService,
	private readonly shellService: ShellService
	)
	{
		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) => status === InteractionStatus.None
				),
				takeUntil(this.pDestroying)
			)
			.subscribe(() =>
			{
				this.setLoginDisplay();
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
				),
				takeUntil(this.pDestroying)
			)
			.subscribe((result: EventMessage) =>
			{
				const payload = result.payload as AuthenticationResult;
				this.msalService.instance.setActiveAccount(payload.account);
			});

		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None)
			)
			.subscribe(() =>
			{
				this.checkAndSetActiveAccount();
				this.getClaims(
					this.msalService.instance.getActiveAccount()?.idTokenClaims
				);
			});
	}

	private setLoginDisplay()
	{
		this.isLoggedIn.next(this.msalService.instance.getAllAccounts().length > 0);
	}

	public getFetchedPermission(group: RoutePermissionGroup): RoutePermissions | undefined
	{
		const key: string = this.getKeyFromPermissionGroup(group);
		const permissionsMap: PermissionsMap = this.fetchedPermissions$.value;
		return permissionsMap.get(key);
	}
	public setRoutePermissions(group: RoutePermissionGroup, permissions: RoutePermissions): void
	{
		const key: string = this.getKeyFromPermissionGroup(group);
		const permissionsMap: PermissionsMap = this.fetchedPermissions$.value;
		permissionsMap.set(key, permissions);
		this.fetchedPermissions$.next(permissionsMap);
	}
	private getKeyFromPermissionGroup(group: RoutePermissionGroup): string
	{
		return btoa(`${group.category}${group.action}`);
	}

	public async login(): Promise<void>
	{
		return lastValueFrom(new Observable((subscriber: Subscriber<void>) =>
		{
			this.msalBroadcastService.inProgress$
				.pipe(
					filter(
						(status: InteractionStatus) => status === InteractionStatus.None
					),
					takeUntil(this.pDestroying)
				)
				.subscribe( () =>
				{
					this.fetchedPermissions$.next(new Map<string, RoutePermissions>());
					this.msalService.loginRedirect().subscribe({
						next: () =>
						{
							subscriber.next();
							subscriber.complete();
						},
						error: (error) =>
						{
							this.loggerService.log('Error redirecting to login', error);
							this.localStorageService.clearStorage();
							subscriber.error(error);
							subscriber.complete();
						}
					});
				});
		}));
	}

	public async logout(attempts: number = 3): Promise<void>
	{
		if(attempts < 1)
		{
			this.loggerService.log('Ran out of attempts');
			return Promise.resolve();
		}
		attempts--;
		try
		{
			this.fetchedPermissions$.next(new Map<string, RoutePermissions>());
			this.shellService.shouldLoadPage$.next(true);
			await lastValueFrom(this.msalService.logout());
		}
		catch (error)
		{
			this.shellService.shouldLoadPage$.next(false);
			this.loggerService.log('Error redirecting to logout', error);
			this.localStorageService.clearStorage();
			return this.logout(attempts);
		}
	}

	public async register(): Promise<void>
	{
		try
		{
			const request = {
				scopes: [''],
				authority: b2cPolicies.authorities.signUpSignIn.authority
			};
			await lastValueFrom(this.msalService.loginRedirect(request));
		}
		catch (error)
		{
			this.loggerService.log('Error redirecting to login', error);
			throw error;
		}
	}

	private checkAndSetActiveAccount()
	{
		const activeAccount = this.msalService.instance.getActiveAccount();
		if (
			!activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
		)
		{
			const accounts = this.msalService.instance.getAllAccounts();
			this.msalService.instance.setActiveAccount(accounts[0]);
		}
	}

	private getClaims(
		claims: (TokenClaims & { [key: string]: unknown }) | undefined
	)
	{
		this.dataSource = [
			{
				id: 1,
				claim: 'Given Name',
				value: claims ? claims['given_name'] : null
			},
			{
				id: 1,
				claim: 'Family Name',
				value: claims ? claims['family_name'] : null
			},
			{ id: 2, claim: 'Object ID', value: claims ? claims['oid'] : null },
			{ id: 3, claim: 'Job Title', value: claims ? claims['jobTitle'] : null },
			{ id: 4, claim: 'City', value: claims ? claims['city'] : null }
		];
	}
	public static extractPermissionsFromRoute(router: ActivatedRouteSnapshot): RoutePermissionGroup
	{
		const routePermissionsPath: string = 'data.permissions';
		const defaultPermissions: RoutePermissionGroup = { category: 'NONE' as RoutePermissionCategory, action: 'NONE' as RoutePermissionAction };
		return lodashGet(router, routePermissionsPath, defaultPermissions);
	}
	public getRoutePermissions(route: ActivatedRouteSnapshot): Observable<RoutePermissions>
	{
		const group: RoutePermissionGroup = AuthenticationService.extractPermissionsFromRoute(route);
		return new Observable( subscriber =>
		{
			this.apiService.getRoutePermissions(group).subscribe({
				next: (response: RoutePermissions | ApiResponse) =>
				{
					const responseHasRoutePermissions: boolean = lodashHasIn(response, 'actions');
					if (responseHasRoutePermissions)
					{
						subscriber.next(response as RoutePermissions);
						subscriber.complete();
					}
					else
					{
						subscriber.error(response as ApiResponse);
						subscriber.complete();
						this.loggerService.log('no actions in response', lodashCloneDep(response));
					}

				},
				error: (err: unknown) =>
				{
					this.loggerService.log('Error\n', err);
					subscriber.error(err);
					subscriber.complete();
				}
			});
		});
	}
}
